import { Dialog, DialogContent, DialogActions, Typography, Box, Divider, Chip } from '@mui/material'
import { useTranslator } from '@/hooks'
import { Button } from '@/@core/components/mui/Button'
import Link from '@/components/Link'
import { AttendanceStatusEnum, BalanceStatusEnum } from '@/@data'
import dayjs from 'dayjs'

interface AttendanceData {
  id: number
  month: string
  user: {
    first_name: string
    last_name?: string
    employee_id: string
  }
  attendance: Array<{
    date: number
    status: 'absent' | 'working_day' | 'non_working_day'
    day_of_month: string
  }>
  attendance_status: {
    absent: number
    non_working_day: number
    working_day: number
  }
  working_day: number
  day_start_of_month: number
}

interface TransactionEmployeeProps {
  open: boolean
  onClose: () => void
  employee: any,
  data: AttendanceData,
  dataCompany?: any
}

export const TransactionEmployee = ({
  open,
  onClose,
  employee,
  data,
  dataCompany
}: TransactionEmployeeProps) => {
  const { __ } = useTranslator()

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'working_day':
        return {
          color: '#77a0ff'
        }
      case 'absent':
        return {
          color: '#ff9497'
        }
      case 'non_working_day':
        return {
          color: '#c0bec5'
        }
      default:
        return {
          color: '#E0E0E0'
        }
    }
  }

  // Use actual attendance data length instead of calendar month days
  const daysInMonth = data?.attendance.length

  // day_start_of_month: 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat, 0=Sun
  const startDay = data?.day_start_of_month === 0 ? 6 : data?.day_start_of_month - 1

  const weeks = []
  const totalCells = Math.ceil((daysInMonth + startDay) / 7) * 7

  for (let i = 0; i < totalCells; i += 7) {
    const week = []

    for (let j = 0; j < 7; j++) {
      const cellIndex = i + j
      const dayNumber = cellIndex - startDay + 1

      if (cellIndex < startDay || dayNumber > daysInMonth) {
        week.push(null)
      } else {
        week.push(dayNumber)
      }
    }

    weeks.push(week)
  }

  const getAttendanceForDay = (day: number) => {
    return data.attendance.find((att: any) => att.date === day)
  }

  const formatDayOfMonth = (dayOfMonth: string) => {
    const date = new Date(dayOfMonth)
    const day = date.getDate()

    return `${day}`
  }

  const currentStatus = dataCompany?.status


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      scroll='body'
      fullWidth
      closeAfterTransition={false}
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '700px' }
      }}
    >

      <DialogContent className='p-0 m-0' sx={{ padding: 0 }}>
        <div className='p-5'>
          <div className='flex items-center gap-2'>
            <div className='text-[24px] font-medium text-[#2f2b3de6]'>
              {dataCompany?.month ? dayjs(dataCompany.month).format('MMMM YYYY') : ''}
            </div>
            <Chip
              label={currentStatus}
              size='small'
              className='capitalize rounded-full p-4'
              classes={
                currentStatus === AttendanceStatusEnum.Unreconciled
                  ? {
                      root: 'bg-red-50 text-red-600 border-red-200',
                      label: 'text-red-600'
                    }
                  : currentStatus === AttendanceStatusEnum.Reconciled
                    ? {
                        root: 'bg-green-50 text-green-600 border-green-200',
                        label: 'text-green-600'
                      }
                    : currentStatus === AttendanceStatusEnum.Checking
                      ? {
                          root: 'bg-blue-50 text-blue-600 border-blue-200',
                          label: 'text-blue-600'
                        }
                      : currentStatus === BalanceStatusEnum.Paid
                        ? {
                            root: 'bg-[#CCF2DD] text-[#28C76F] border-green-200',
                            label: 'text-green-600'
                          }
                        : currentStatus === BalanceStatusEnum.Pending
                          ? {
                              root: 'bg-[#F69F43] text-[#fff] border-blue-200',
                              label: 'text-white'
                            }
                          : currentStatus === BalanceStatusEnum.Partial
                            ? {
                                root: 'bg-[#FF9F43] text-[#FFF3E0] border-orange-200',
                                label: 'text-[#FFF3E0]'
                              }
                            : currentStatus === BalanceStatusEnum.Unpaid
                              ? {
                                  root: 'bg-[#EA5455] text-[#FFEBEE] border-orange-200',
                                  label: 'text-[#FFEBEE]'
                                }
                              : currentStatus === 'estimate balance'
                                ? {
                                    root: 'bg-[#CFD5FC] text-[#3551F2] border-blue-200',
                                    label: 'text-blue-600'
                                  }
                                : {
                                    root: 'bg-gray-50 text-gray-600 border-gray-200',
                                    label: 'text-gray-600'
                                  }
              }
              variant='tonal'
            />
          </div>
        </div>
        <Divider />
        <div className='flex items-center gap-3 p-5'>
          <div className='flex items-center gap-1'>
            <div className='w-[16px] h-[16px] rounded-full bg-[#577dfd]'></div>
            <Typography variant='body2' className='text-[14px] font-normal text-[#2f2b3de6]'>
              {__('On')}
            </Typography>
          </div>
          <div className='flex items-center gap-1'>
            <div className='w-[16px] h-[16px] rounded-full bg-[#ff7074]'></div>
            <Typography variant='body2' className='text-[14px] font-normal text-[#2f2b3de6]'>
              {__('Off')}
            </Typography>
          </div>
          <div className='flex items-center gap-1'>
            <div className='w-[16px] h-[16px] rounded-full bg-[#c0bec5]'></div>
            <Typography variant='body2' className='text-[14px] font-normal text-[#2f2b3de6]'>
              {__('Non-Working Day')}
            </Typography>
          </div>
        </div>
        <div>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  border: '1px solid #e0e0e0'
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#eaeaec' }}>
                    {[__('Mon'), __('Tue'), __('Wed'), __('Thu'), __('Fri'), __('Sat'), __('Sun')].map((day, index) => (
                      <th
                        key={index}
                        style={{
                          padding: '12px 8px',
                          textAlign: 'center',
                          borderRight: '1px solid #e0e0e0',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '300',
                          fontSize: '13px',
                          minWidth: '80px'
                        }}
                      >
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {weeks.map((week, weekIndex) => (
                    <tr key={weekIndex}>
                      {week.map((day, dayIndex) => {
                        const attendance = day ? getAttendanceForDay(day) : null
                        const statusConfig = attendance ? getStatusConfig(attendance.status) : null

                        // Determine background color based on conditions
                        const getBackgroundColor = () => {
                          if (day === null) return '#f9f9f9'
                          if (dataCompany?.minimum_working_days && day <= dataCompany?.minimum_working_days) return '#2e263d0a'
                          const currentDayOfMonth = attendance?.day_of_month
                            ? new Date(attendance.day_of_month).getDate()
                            : day
                          if (
                            dataCompany?.company?.withdraw_cutoff_day &&
                            dataCompany?.company?.withdraw_cutoff_day > 0 &&
                            currentDayOfMonth > dataCompany?.company?.withdraw_cutoff_day
                          )
                            return '#2e263d0a'

                          return 'transparent'
                        }

                        return (
                          <td
                            key={dayIndex}
                            style={{
                              padding: '8px',
                              textAlign: 'center',
                              borderRight: '1px solid #e0e0e0',
                              borderBottom: '1px solid #e0e0e0',
                              minHeight: '60px',
                              verticalAlign: 'middle',
                              backgroundColor: getBackgroundColor()
                            }}
                          >
                            {day && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}
                              >
                                <Typography
                                  variant='body2'
                                  sx={{
                                    fontWeight: '500',
                                    fontSize: '13px',
                                    color: '#2f2b3de6'
                                  }}
                                >
                                  {attendance?.day_of_month ? formatDayOfMonth(attendance.day_of_month) : day}
                                </Typography>
                                {statusConfig && (
                                  <div
                                    className='w-[16px] h-[16px] rounded-full'
                                    style={{ backgroundColor: statusConfig.color }}
                                  ></div>
                                )}
                              </Box>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        </div>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', p: 3, mt: 6 }}>
        <Button
          onClick={onClose}
          variant='contained'
          sx={{ mr: 2, color: '#6F83F6', background: '#6F83F629', height: 38 }}
          className='shadow-none'
        >
          <span className='text-[15px] font-medium'>
            {__('Cancel')}
          </span>
        </Button>
        <Link href={`/cms/attendance/employee/${employee?.id}?companyId=${employee?.company_id}`}>
          <Button variant='contained' sx={{ borderRadius: 10, height: 38, backgroundColor: '#6F83F6' }} className='shadow-none'>
            <span className='text-[15px] font-medium'>
              {__('Edit')}
            </span>
          </Button>
        </Link>
      </DialogActions>
    </Dialog>
  )
}

export default TransactionEmployee
